/**
 * anthropicClient.ts — Wrapper für Anthropic Messages API (Claude).
 *
 * Analog zu mistralClient.ts, aber für api.anthropic.com.
 *
 * Hosting: api.anthropic.com (US-Headquarters, US/EU-Inference je nach Modell-
 * Region). Auth via x-api-key aus VITE_ANTHROPIC_API_KEY.
 *
 * DSGVO-Hinweis: Anthropic ist US-Unternehmen mit DPA + EU-Standardvertrags-
 * klauseln. Für Pilot-Tests OHNE echte Mandantendaten ist das akzeptabel,
 * für Live-Verarbeitung von Sozialdaten muss der DSB die Subprozessor-
 * Aufnahme bewerten und ein AVV mit Anthropic PBC vorliegen. Bis dahin:
 * nur Test-Modus mit pseudonymisierten Daten + synthetischen Test-Cases.
 *
 * Sicherheits-Garantien identisch zu mistralClient:
 *   1. Doppel-Gate (feature_flag + VITE_ANTHROPIC_API_KEY)
 *   2. PII-Pattern-Re-Check VOR Call (defense-in-depth)
 *   3. Audit-Log + llm_usage_log für jeden Call
 *   4. API-Key nie ins UI/Log leaken
 *
 * CORS: Anthropic erlaubt Browser-Direktzugriff nur mit dem Header
 * `anthropic-dangerous-direct-browser-access: true`. Setzen wir, da
 * wir aktuell ohne Proxy-Backend arbeiten. Bei Skalierung: eigener
 * Cloudflare-Worker als Proxy (Sprint 4).
 */

import { getFeatureFlag } from '../supabaseFeatureFlags';
import { logLlmUsage } from '../supabaseLlmUsage';
import type { LlmCallStatus, LlmProvider } from '../supabaseTypes';
import { containsPii, sha256Hex } from './mistralClient';

// --- Konfiguration --------------------------------------------------------

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_VERSION = '2023-06-01';
const DEFAULT_TEMPERATURE = 0.3;
const DEFAULT_MAX_TOKENS = 1024;
const REQUEST_TIMEOUT_MS = 60_000;
const FEATURE_FLAG_KEY = 'llm_suggestions';

// --- API-Key-Helper -------------------------------------------------------

function readApiKey(): string | undefined {
  const env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) as
    | Record<string, string | undefined>
    | undefined;
  const k = env?.VITE_ANTHROPIC_API_KEY;
  if (typeof k !== 'string' || k.trim().length === 0) return undefined;
  return k.trim();
}

/**
 * @returns true wenn VITE_ANTHROPIC_API_KEY im Build gesetzt ist.
 */
export function isAnthropicConfigured(): boolean {
  return readApiKey() !== undefined;
}

// --- Error-Codes ----------------------------------------------------------

export const AnthropicErrorCode = {
  ANTHROPIC_NOT_CONFIGURED: 'anthropic_not_configured',
  LLM_FLAG_DISABLED: 'llm_flag_disabled',
  PII_DETECTED: 'pii_detected',
  HTTP_ERROR: 'http_error',
  TIMEOUT: 'timeout',
  PARSE_ERROR: 'parse_error',
} as const;

export type AnthropicErrorCodeValue = (typeof AnthropicErrorCode)[keyof typeof AnthropicErrorCode];

export class AnthropicError extends Error {
  public readonly code: AnthropicErrorCodeValue;
  public readonly httpStatus: number | undefined;

  constructor(code: AnthropicErrorCodeValue, message: string, httpStatus?: number) {
    super(message);
    this.code = code;
    this.httpStatus = httpStatus;
    this.name = 'AnthropicError';
  }
}

// --- Public API -----------------------------------------------------------

export interface AnthropicOptions {
  readonly model: LlmProvider;
  readonly temperature?: number;
  readonly maxTokens?: number;
  readonly caseId?: string | null;
  readonly fieldKey: string;
  readonly fetchImpl?: typeof fetch;
}

export interface AnthropicResponse<T = string> {
  readonly content: T;
  readonly promptTokens: number;
  readonly completionTokens: number;
  readonly totalTokens: number;
  readonly durationMs: number;
}

async function safeLog(args: {
  caseId: string | null;
  fieldKey: string;
  provider: LlmProvider;
  pseudonymizedHash: string;
  status: LlmCallStatus;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  durationMs?: number;
  errorMessage?: string;
}): Promise<void> {
  try {
    await logLlmUsage(args);
  } catch (e) {
    console.error('anthropicClient.safeLog: logLlmUsage warf', e);
  }
}

async function preflightOrThrow(opts: {
  caseId: string | null;
  fieldKey: string;
  provider: LlmProvider;
  textToSend: string;
}): Promise<{ apiKey: string; hash: string }> {
  // 1. Flag-Check
  let flagEnabled = false;
  try {
    flagEnabled = await getFeatureFlag(FEATURE_FLAG_KEY);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'unknown';
    throw new AnthropicError(
      AnthropicErrorCode.LLM_FLAG_DISABLED,
      `Flag-Check fehlgeschlagen: ${msg}`,
    );
  }
  if (!flagEnabled) {
    const hash = await sha256Hex(opts.textToSend);
    await safeLog({
      caseId: opts.caseId,
      fieldKey: opts.fieldKey,
      provider: opts.provider,
      pseudonymizedHash: hash,
      status: 'blocked_flag',
      errorMessage: 'feature_flag llm_suggestions ist deaktiviert',
    });
    throw new AnthropicError(
      AnthropicErrorCode.LLM_FLAG_DISABLED,
      'LLM-Vorschläge sind im System deaktiviert (feature_flag llm_suggestions = false).',
    );
  }

  // 2. API-Key-Check
  const apiKey = readApiKey();
  if (!apiKey) {
    throw new AnthropicError(
      AnthropicErrorCode.ANTHROPIC_NOT_CONFIGURED,
      'VITE_ANTHROPIC_API_KEY ist nicht gesetzt — Anthropic/Claude-Integration nicht verfügbar.',
    );
  }

  // 3. PII-Pattern-Re-Check
  if (containsPii(opts.textToSend)) {
    const hash = await sha256Hex(opts.textToSend);
    await safeLog({
      caseId: opts.caseId,
      fieldKey: opts.fieldKey,
      provider: opts.provider,
      pseudonymizedHash: hash,
      status: 'blocked_pseudonymization',
      errorMessage: 'PII-Pattern im pseudonymisierten Input erkannt',
    });
    throw new AnthropicError(
      AnthropicErrorCode.PII_DETECTED,
      'Input enthält PII-Pattern trotz Pseudonymisierung — Call abgebrochen. Bug an Entwickler melden.',
    );
  }

  const hash = await sha256Hex(opts.textToSend);
  return { apiKey, hash };
}

/**
 * Klassischer Messages-Call. Liefert reinen Text zurück.
 *
 * @throws AnthropicError mit programmatisch auswertbarem `code`-Feld.
 */
export async function callAnthropic(
  systemPrompt: string,
  userPrompt: string,
  opts: AnthropicOptions,
): Promise<AnthropicResponse<string>> {
  const caseId = opts.caseId ?? null;
  const provider = opts.model;
  const textToSend = systemPrompt + '\n' + userPrompt;

  const { apiKey, hash } = await preflightOrThrow({
    caseId,
    fieldKey: opts.fieldKey,
    provider,
    textToSend,
  });

  const fetchImpl = opts.fetchImpl ?? fetch;
  const start = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const resp = await fetchImpl(ANTHROPIC_API_URL, {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'anthropic-version': ANTHROPIC_VERSION,
        // Browser-Direktzugriff erlauben (für Test-Modus). Bei Live-Deploy
        // sollte das über einen Cloudflare-Worker-Proxy laufen, dann ohne
        // diesen Header.
        'anthropic-dangerous-direct-browser-access': 'true',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: opts.model,
        temperature: opts.temperature ?? DEFAULT_TEMPERATURE,
        max_tokens: opts.maxTokens ?? DEFAULT_MAX_TOKENS,
        system: systemPrompt,
        messages: [{ role: 'user', content: userPrompt }],
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const duration = Date.now() - start;
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      const errMsg = `HTTP ${String(resp.status)} ${resp.statusText} — ${body.slice(0, 200)}`;
      await safeLog({
        caseId,
        fieldKey: opts.fieldKey,
        provider,
        pseudonymizedHash: hash,
        status: 'error',
        durationMs: duration,
        errorMessage: errMsg,
      });
      throw new AnthropicError(AnthropicErrorCode.HTTP_ERROR, errMsg, resp.status);
    }

    const json = (await resp.json()) as AnthropicRawResponse;
    // Anthropic content ist ein Array von Blocks; wir nehmen den ersten text-Block
    const firstTextBlock = json.content?.find((b) => b.type === 'text');
    const content = firstTextBlock?.text;
    if (typeof content !== 'string' || content.length === 0) {
      await safeLog({
        caseId,
        fieldKey: opts.fieldKey,
        provider,
        pseudonymizedHash: hash,
        status: 'error',
        durationMs: duration,
        errorMessage: 'Anthropic-Response ohne text-content',
      });
      throw new AnthropicError(
        AnthropicErrorCode.PARSE_ERROR,
        'Anthropic-Response ohne text-content',
      );
    }

    const usage = json.usage ?? { input_tokens: 0, output_tokens: 0 };
    const totalTokens = usage.input_tokens + usage.output_tokens;
    await safeLog({
      caseId,
      fieldKey: opts.fieldKey,
      provider,
      pseudonymizedHash: hash,
      status: 'success',
      promptTokens: usage.input_tokens,
      completionTokens: usage.output_tokens,
      totalTokens,
      durationMs: duration,
    });

    return {
      content,
      promptTokens: usage.input_tokens,
      completionTokens: usage.output_tokens,
      totalTokens,
      durationMs: duration,
    };
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof AnthropicError) throw e;
    const isAbort = e instanceof Error && e.name === 'AbortError';
    const code = isAbort ? AnthropicErrorCode.TIMEOUT : AnthropicErrorCode.HTTP_ERROR;
    const msg = e instanceof Error ? e.message : 'unknown fetch error';
    await safeLog({
      caseId,
      fieldKey: opts.fieldKey,
      provider,
      pseudonymizedHash: hash,
      status: 'error',
      durationMs: Date.now() - start,
      errorMessage: `${code}: ${msg}`,
    });
    throw new AnthropicError(code, msg);
  }
}

// --- Internal types -------------------------------------------------------

interface AnthropicRawResponse {
  content?: ReadonlyArray<{ type: string; text?: string }>;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
}
